import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute,  Params, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { SnackBarTheme } from 'src/app/shared/models/snackbar-theme.enum';
import { LetterOfCreditService } from 'src/app/shared/services/letter-of-credit/letter-of-credit.service';
import { TranslationConstants } from 'src/app/shared/services/util/translation.constants';
import { SimpleContactModalComponent } from 'src/app/shared/simple-contact-modal/simple-contact-modal.component';
import { SnackbarComponent } from 'src/app/shared/snackbar/snackbar.component';
import { v4 as uuid } from 'uuid';
import { ModalRequestComponent } from '../modal-request/modal-request.component';
import { AccountService } from 'src/app/shared/services/account/account.services';
import BigNumber from 'bignumber.js';
import { RequestDocument } from 'src/app/shared/models/request-document.model';
import { ShoppingCartService } from 'src/app/shared/services/cart/shopping-cart.service';
import { FileService } from 'src/app/shared/services/util/file.service';
import { Requests } from 'src/app/shared/models/Requests';
import { RequestHistory } from 'src/app/shared/models/RequestHistory';
import { RequestStatus } from 'src/app/shared/models/RequestsStatusEnum';
import { ValueConverterService } from 'src/app/shared/services/util/value-converter.service';
import { OnkeypressService } from 'src/app/shared/services/util/onkeypress.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { BottomSheetComponent } from 'src/app/shared/bottom-sheet/bottom-sheet.component';
import { ConfigReaderService } from 'src/app/shared/services/util/config.reader.service';
import { EContactChannels, IContactDetails } from 'src/app/shared/models/IContactDetails';
import { TokensService } from 'src/app/shared/services/tokens/token.service';
import { FileStorageConfidentiality, FileStorageDocumentType } from 'src/app/shared/models/file.model';

@Component({
    selector: 'app-new',
    templateUrl: './new.component.html',
    styleUrls: ['./new.component.scss', '../panel/panel.component.scss']
})
export class NewComponent implements OnInit {

    public userName: string = "";
    public loading: boolean = false;
    public new: boolean = false;
    public view: boolean = false;
    public newValueAnswered: boolean;
    public filesSizeError: boolean;
    public filesSize: number = 0;
    public maxSize = 10000000; // 10MB
    public requestAmount: number;
    public tokensAmount: string = '0,00';
    public tokenPrice: BigNumber = new BigNumber(0);
    public filesUploadedTable: Array<RequestDocument> = [];
    public documentRequest: Array<RequestDocument> = [];
    public displayedColumns: Array<string> = ['name', 'date', 'select' ];
    public dataSource: MatTableDataSource<RequestDocument>;
    public creditLetter: Requests = new Requests();
    public requestedValueNumber: string = "";
    private paramsSubscription: Subscription;
    private contactDetails: IContactDetails = undefined;
    public enableContactButton: boolean = false;

    constructor(
        private readonly snackBarComponent: SnackbarComponent,
        private readonly translationConstants: TranslationConstants,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly letterOfCreditService: LetterOfCreditService,
        private readonly shoppingCartService: ShoppingCartService,
        private readonly fileService: FileService,
        private readonly dialog: MatDialog,
        private readonly accountService: AccountService,
        private readonly valueConverter: ValueConverterService,
        private readonly bottomSheet: MatBottomSheet,
        private readonly myConfigReaderService: ConfigReaderService,
        private readonly tokenService: TokensService,
        private readonly keypressService: OnkeypressService // used in HTML
    ) { }

    public ngOnChanges(): void {
        this.initCreditLetterDetails();
    }

    public ngOnInit(): void {
        this.dataSource = new MatTableDataSource<RequestDocument>(this.filesUploadedTable);
        this.userName = "";
        this.accountService.getUserDetails().subscribe((user: any) => {
            if (user?.firstName) {
                this.userName = user.firstName;
            }
        });

        this.initCreditLetterDetails();

        this.tokenService.getTokens().subscribe(tokens => {
            let mainToken = tokens.find(tkn => tkn.isMain);
            forkJoin([
                this.shoppingCartService.quotations(mainToken?.id),
                this.myConfigReaderService.getContactDetails()
            ]).subscribe(async ([quotation, contactDetails]) => {
                this.tokenPrice = new BigNumber(quotation.amount || 0);
                this.contactDetails = new IContactDetails(contactDetails);
                this.enableContactButton = this.contactDetails.enableContactButton();
            });
        });

        
    }

    public initCreditLetterDetails(): void {
        const url = this.router.url;
        this.view = false;
        this.new = false;

        if (url.match('/view')) {
            this.view = true;
        } else if (url.match('/new')) {
            this.new = true;
        }
        if (!this.new) {
            this.paramsSubscription = this.activatedRoute.params.subscribe(
                (params: Params) => {
                    const number: string = params['id'];
                    if (number) {
                        this.loading = true;
                        this.letterOfCreditService.getRequest(number).subscribe(letter => {
                            if (letter) {
                                this.creditLetter = letter;
                                this.filesUploadedTable = this.creditLetter.documentList;
                                this.dataSource.data = this.filesUploadedTable;
                                this.tokenPrice = new BigNumber(this.creditLetter.tokenQuotation);
                                this.requestedValueNumber = this.creditLetter.valueRequested as any;
                                this.convertTokens(this.requestedValueNumber);
                                this.creditLetter.history = this.sortByDateTime(this.creditLetter.history);
                                this.newValueAnswered = new BigNumber(this.creditLetter.valueOffered)?.isPositive();
                            }
                        }, err => {
                            this.snackBarComponent.openSnackBar(this.translationConstants.translate('creditLetter.snackbar.error.undefined'), SnackBarTheme.error, 4000);
                            this.router.navigate(['/credit-conversion/panel']);
                        }).add(() => this.loading = false);
                    } else {
                        this.loading = false;
                    }
                }
            );
        }
    }

    public mayEditByStatus(): boolean{
        return this.creditLetter.currentStatus === RequestStatus.S || this.creditLetter.currentStatus === RequestStatus.R;
    }

    public sortByDateTime(list: Array<RequestHistory>): Array<RequestHistory> {
        const sortedList = list.sort((a, b) => {
            return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
        });
        return sortedList.reverse();
    }

    public blockNegativeNumber(event: any, value: number): void {
        if (value == 0) {
            event.preventDefault();
        }
    }

    public onKeyPressEvent(event: any){
        this.keypressService.onlyAllowNumbers(event);
    }

    public convertTokens(amountMoney: number | string | BigNumber): void {
        let amountInBigNumber: BigNumber = new BigNumber(0);
        if (!BigNumber.isBigNumber(amountMoney)) {
            amountInBigNumber = this.valueConverter.fromStringFormatToBigNumber(amountMoney.toString());
        }
        this.tokensAmount = amountInBigNumber.dividedBy(this.tokenPrice).toFormat(6);
    }

    public addNewFile(files: FileList): void {
        if ((files?.length + this.dataSource?.data?.length) > 30) {
            this.snackBarComponent.openSnackBar( this.translationConstants.translate('creditLetter.new.fileLimit'), SnackBarTheme.error, 4000 );
            return;
        }

        if (files?.length) {
            if (!this.mapDocuments(files, true)){
                return;
            }
        }

        this.filesSizeError = this.filesSize > this.maxSize;
        if (this.filesSizeError) {
            this.snackBarComponent.openSnackBar( this.translationConstants.translate('creditLetter.new.fileLimit'), SnackBarTheme.error, 4000 );
        }
    }

    public openBottomSheet(element: string): void {
        const sheetRef: any = this.bottomSheet.open(BottomSheetComponent, {
            data: {
                text: this.translationConstants.translate('creditLetter.bottomSheet.confirmDeleteFile'),
                declineOption: this.translationConstants.translate('creditLetter.bottomSheet.cancel'),
                confirmOption: this.translationConstants.translate('creditLetter.bottomSheet.delete'),
            }
        });
        sheetRef.afterDismissed().subscribe(action => {
            if (action) {
                this.snackBarComponent.openSnackBar( this.translationConstants.translate('creditLetter.bottomSheet.removedFile'), SnackBarTheme.success, 4000 );
                this.removeFile(element);
            }
        }, error => {
            this.snackBarComponent.openSnackBar( this.translationConstants.translate('creditLetter.bottomSheet.removeError'), SnackBarTheme.error, 4000 );
        });
    }

    public removeFile(docId: string): void{
        this.creditLetter.documentList = this.creditLetter?.documentList.filter(doc => doc.id !== docId);
        this.filesUploadedTable = this.creditLetter.documentList;
        this.dataSource.data = this.filesUploadedTable;
    }

    // Verificar tipo do "documents"
    public mapDocuments(files: FileList | any[], load: boolean): boolean {
        for (let i = 0; i < files.length; i++) {

            this.filesSize += files[i].size;

            if (load) {
                const reader = new FileReader();
                const file = files[i];
                let normFileName: string = this.normalizeFileName(file.name);
                reader.readAsDataURL(file);
                reader.onload = (event) => {
                    this.fileService.uploadFile({
                        file: event.target.result,
                        name: normFileName,
                        confidentiality: FileStorageConfidentiality.SECURED,
                        documentType: FileStorageDocumentType.CREDIT_LETTER
                    }).subscribe(data => {
                        this.documentRequest.push({
                            id: uuid(),
                            url: data?.value,
                            providedAt: new Date()
                        });
                        this.filesUploadedTable.push(
                            {
                                url: data.value,
                                providedAt: new Date(),
                                size: file.size,
                                id: uuid(),
                                name: normFileName
                            }
                        );
                        this.dataSource.data = this.filesUploadedTable;
                    }, error => {
                        this.snackBarComponent.openSnackBar(this.translationConstants.translate('creditLetter.docUploadError'), SnackBarTheme.error, 4000);
                        return false;
                    })
                };
            }
        }

        this.filesSizeError = this.filesSize > this.maxSize; // in MB
        return true;
    }

    public normalizeFileName(fileName: string): string{
        return fileName.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z][\-\_])/g, '');
    }

    public formatFileSize(size: any): string {
        if (typeof size === 'number') {
            const fileSize = size / (1000 * 1000); // in MB

            return fileSize >= 1 ? `${fileSize.toFixed(2)}MB` : `${(fileSize*1000).toFixed(2)}KB`;
        }
    }

    public getNameFromURL(url: string): string{
        let name: string = '';
        if (url && url.indexOf('/')){
            name = url.split('/').pop();
        }
        return name;
    }

    public deleteItem(size: number, index: number): void {

        this.filesSize -= size;

        this.filesUploadedTable = this.filesUploadedTable.filter((item, i) => index !== i);
        this.dataSource.data = this.filesUploadedTable;
        this.filesSizeError = this.filesSize > this.maxSize;
    }

    public backToPanel(): void {
        this.router.navigate(['/credit-conversion/panel']);
    }

    public createNewRequest(): void {
        this.loading = true;
        this.creditLetter.id = uuid();
        this.creditLetter.createdAt = new Date();
        this.creditLetter.currentStatus = RequestStatus.S;
        this.creditLetter.documentList = this.documentRequest;
        this.creditLetter.tokenQuotation = this.tokenPrice;
        this.creditLetter.history = [];
        this.creditLetter.valueRequested = this.valueConverter.handlingMonetaryAmount(this.requestedValueNumber);
        this.letterOfCreditService.createNewRequest(this.creditLetter).subscribe(success => {
            this.snackBarComponent.openSnackBar(this.translationConstants.translate('creditLetter.new.requestCreatedSuccess'), SnackBarTheme.success, 4000);
            this.router.navigate(['/credit-conversion/panel']);
        }, err => {
            this.snackBarComponent.openSnackBar(this.translationConstants.translate('creditLetter.new.creationError'), SnackBarTheme.error, 4000);
        }).add(() => this.loading = false);
    }

    // When the status in "New"
    public updateRequest(): void {
        const requestedValue: BigNumber = new BigNumber(this.valueConverter.handlingMonetaryAmount(this.requestedValueNumber).toString().replace('.', ''));
        if (requestedValue && requestedValue.isGreaterThan(0)) {
            this.creditLetter.valueRequested = requestedValue;
            this.updateLetter('requestSavedSuccess');
        } else {
            this.snackBarComponent.openSnackBar(this.translationConstants.translate('creditLetter.snackbar.error.undefined'), SnackBarTheme.error, 4000);
        }
    }

    // For all updates
    public updateLetter(successMessage: string): void {
        this.loading = true;

        this.letterOfCreditService.updateLetter(this.creditLetter).subscribe(success => {
            this.snackBarComponent.openSnackBar(this.translationConstants.translate(`creditLetter.new.${successMessage}`), SnackBarTheme.success, 4000);
            this.backToPanel();
        }, error => {
            this.snackBarComponent.openSnackBar(this.translationConstants.translate(`creditLetter.snackbar.error.${error.code}`), SnackBarTheme.error, 4000);
        }).add(() => this.loading = false);
    }

    public deny(): void {
        this.openModalRequest('deny');
    }

    public answer(): void {
        this.openModalRequest('answer');
    }

    public approve(): void {
        this.openModalRequest('approve');
    }

    public openModalRequest(title: string): void {
        const dialogRef = this.dialog.open(ModalRequestComponent, {
            panelClass: 'modal-letter-request',
            data: {
                title,
                letter: this.creditLetter
            }
        });

        dialogRef.afterClosed().subscribe((data: {operation: string, letter: Requests}) => {
            let serviceSuccess: boolean = false;
            let operationMessage: string;

            if (data) {
                if (data.operation === 'deny') {
                    operationMessage = 'requestDeniedSuccess';
                } else if (data.operation === 'approve') {
                    operationMessage = 'requestApprovedSuccess';
                } else if (data.operation === 'answer') {
                    operationMessage = 'answeredSuccess';
                }
                this.updateLetter(operationMessage);
            }
        });
    }

    public openContactModal(): void {
        const channel: EContactChannels = this.contactDetails.channelToUse();
        if (EContactChannels.EMAIL === channel){
            this.dialog.open( SimpleContactModalComponent, {
                panelClass: 'customDialog',
                data: {
                    contactDetails: this.contactDetails
                }        
            } );
        }else if (EContactChannels.CHAT === channel){
            window.open(this.contactDetails.tawkTo);
        }
    }

    public bigToNumber(value: BigNumber): number {
        return value ? value.toNumber() : 0;
    }

    public editLetter(letterNumber: number): void {
        this.router.navigate([`/credit-conversion/edit/${letterNumber}`])
    }

    public disablingInput(): boolean {
        if (!this.new && this.creditLetter?.currentStatus != 'STARTED' || this.view) {
            return true;
        } else {
            return false;
        }
    }
}
